import type * as Stitches from '@stitches/react';

import { SVGMol } from '../../../../../visualization/Compound/SVGMol';
import { SVGMolSpace } from '../../../../../visualization/Compound/SVGMolSpace';
import { fadeIn } from '../../../../../../stitches.animations';
import { css, css as generateCss, styled } from '../../../../../../stitches.config';
import { Button } from '../../../../Button/Button';
import { useMolDrawing } from '../../../hooks/useMolDrawing';
import { CommonMol } from '../services/getCommonMols';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipPortal,
  TooltipTrigger,
} from '../../../../../containers/Tooltip';

const fadeInClass = css({
  animation: `${fadeIn} .3s both`,
});

const FragmentIcon = styled('span', {
  pointerEvents: 'none',
});

type FragmentProps = {
  css?: Stitches.CSS;
  mol: CommonMol;
  size: number;
  onPickAtom: (mol: CommonMol, defaultIdx: string) => void;
  onPickBond: (mol: CommonMol, defaultIdx: string) => void;
  active?: boolean;
  renderDelay?: number;
};
export const Fragment = ({ css, mol, size, onPickAtom, onPickBond, active, renderDelay = 0 }: FragmentProps) => {
  const drawing = useMolDrawing({ mol, renderDelay });

  const onPickFragment = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (Number.isFinite(mol.defaultAtomPickIdx)) {
      onPickAtom(mol, String(mol.defaultAtomPickIdx));
    } else if (Number.isFinite(mol.defaultBondPickIdx)) {
      onPickBond(mol, String(mol.defaultBondPickIdx));
    } else {
      const doubleBonds = Object.values(mol.bonds).filter(bond => {
        return bond.order > 1;
      });

      const [firstBond] = [...doubleBonds, ...Object.values(mol.bonds)];
      onPickBond(mol, firstBond.idx);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <Button
          as={TooltipTrigger}
          weight="minimal"
          size="small"
          onClick={onPickFragment}
          css={css}
          text={
            <FragmentIcon>
              {drawing == null ? (
                <div style={{ width: size, height: size }} />
              ) : (
                <SVGMolSpace viewport={drawing.viewport} width={size} height={size} className={fadeInClass()}>
                  <SVGMol drawnMol={drawing.drawnMol} showWarnings={false} />
                </SVGMolSpace>
              )}
            </FragmentIcon>
          }
          data-test={`common-fragment-button-${mol.key}`}
          active={active}
          className={generateCss({
            padding: '$2 !important',
            aspectRatio: 1,
          })()}
        />
        <TooltipPortal>
          <TooltipContent side="left">
            {mol.key}
            <TooltipArrow />
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
};
